import type {ReactElement} from 'react';
import React from 'react';
import {getColorFillTypeFromFillType, getColorObjectArrayFromFillColor, getFillTypeFromColorFillType, getRGBAArrayFromColorStorage} from '@Utils/color.util';
import {PosterEditingBaseColorPanel} from '@Components/poster-editor/components/poster-editing-side-panel/components/poster-editing-base-color-panel';
import type {ColorStorage} from '@Components/color-picker-v2';
import type {ColorFillPropertyType} from '@Components/color-fill';
import {useGetSelectedTranscriptItem} from '@Hooks/poster-editor/useGetSelectedTranscriptItem';
import {useGetKeyFromSubtitlePropertiesForTranscriptItem} from '@Hooks/poster-editor/useGetKeyFromSubtitlePropertiesForTranscriptItem';
import {useGetKeyFromSubtitleBackgroundFillPropertiesForActiveTranscriptItem} from '@Hooks/poster-editor/useGetKeyFromSubtitleBackgroundFillPropertiesForActiveTranscriptItem';

export function TranscriptBackgroundColorPanel(): ReactElement<any> {
  const activeItem = useGetSelectedTranscriptItem();
  if (activeItem === null) {
    throw new Error('Transcript Item is undefined in TranscriptBackgroundColorPanel');
  }

  const backgroundBorderRadius = useGetKeyFromSubtitlePropertiesForTranscriptItem('backgroundBorderRadius');
  if (backgroundBorderRadius === undefined) {
    throw new Error('Transcript item does not have backgroundBorderRadius in its subtitle objects hashmap');
  }

  const bgFillColor = useGetKeyFromSubtitleBackgroundFillPropertiesForActiveTranscriptItem('fillColor');
  if (bgFillColor === undefined) {
    throw new Error('Transcript item subtitle objects do not have a fillColor property in the backgroundFill object');
  }

  const bgFillType = useGetKeyFromSubtitleBackgroundFillPropertiesForActiveTranscriptItem('fillType');
  if (bgFillType === undefined) {
    throw new Error('Transcript item subtitle objects do not have a fillType property in the backgroundFill object');
  }

  const onBackgroundColorChangeEnded = (updatedColor: ColorStorage[]): void => {
    void activeItem.updateAllSubtitlesStylesFromSubtitleObject({
      backgroundFill: {
        fillColor: getRGBAArrayFromColorStorage(updatedColor),
      },
    });
  };

  const onBackgroundColorChange = (updatedColor: ColorStorage[]): void => {
    void activeItem.updateAllSubtitlesStylesFromSubtitleObject(
      {
        backgroundFill: {
          fillColor: getRGBAArrayFromColorStorage(updatedColor),
        },
      },
      {undoable: false}
    );
  };

  const onBackgroundColorTypeChanged = (newType: ColorFillPropertyType): void => {
    if (getFillTypeFromColorFillType(newType) === bgFillType) {
      return;
    }

    void activeItem.updateAllSubtitlesStylesFromSubtitleObject({
      backgroundFill: {
        fillType: getFillTypeFromColorFillType(newType),
        fillColor: activeItem.getAnySubtitle().backgroundFill.getColorForNewType(getFillTypeFromColorFillType(newType)),
      },
    });
  };

  return (
    <PosterEditingBaseColorPanel
      id="transcript-background-fill-color-panel"
      color={getColorObjectArrayFromFillColor(bgFillColor)}
      onChangeColor={onBackgroundColorChange}
      onChangeEnded={onBackgroundColorChangeEnded}
      onChangeType={onBackgroundColorTypeChanged}
      isGradientEnabled
      type={getColorFillTypeFromFillType(bgFillType)}
    />
  );
}
